import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import type {
  CustomMetric,
  CustomMetricsQueryResult,
  SearchParams,
} from '../api/types.js';
import { useEnhancedMutation, useTypedQuery } from '../hooks/index.js';
import {
  getPaginatedDataPlaceholder,
  INFINITYISH,
} from './../constants/tablesConfig.js';

const SYSTEM_METRICS_KEY_SEGMENT = 'headers-meta';

const getCustomMetricsParams = (search = '') => ({
  search: search,
  pageNumber: 1,
  itemsPerPage: INFINITYISH,
});

export const useCustomMetrics = (
  searchParams?: SearchParams,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const baseQueryOptions =
    DataIngestion.CustomMetrics.getCustomMetricsQueryOptions({
      params: getCustomMetricsParams(searchParams?.search),
    });

  return useTypedQuery({
    ...baseQueryOptions,
    placeholderData: getPaginatedDataPlaceholder({
      canBeCreated: false,
      maxCount: 0,
    }),
    select(data) {
      if (searchParams?.pageNumber && searchParams?.itemsPerPage) {
        const start = (searchParams.pageNumber - 1) * searchParams.itemsPerPage;
        const end = start + searchParams.itemsPerPage;
        return {
          ...data,
          items: data.items.slice(start, end),
        };
      }
      return data;
    },
    refetchOnMount: false,
    ...options,
  });
};

export const useCreateCustomMetric = () => {
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  const { queryKey: customMetricsQueryKey } =
    DataIngestion.CustomMetrics.getCustomMetricsQueryOptions({
      params: getCustomMetricsParams(),
    });

  const baseQueryKey = customMetricsQueryKey.slice(0, -1);

  return useEnhancedMutation({
    mutationFn: async (args: {
      payload: DataIngestion.CustomMetrics.CreateCustomMetricPayload;
      onSuccess: (id: string) => void;
    }) => {
      const response = await DataIngestion.CustomMetrics.createCustomMetric({
        data: args.payload,
      });
      return response.data.additional;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: baseQueryKey,
        exact: false,
      });

      const previousValue = queryClient.getQueryData(customMetricsQueryKey);

      return { previousValue };
    },
    onSuccess({ id }, { payload, onSuccess }) {
      onSuccess(id);

      const previousValue = queryClient.getQueryData<CustomMetricsQueryResult>(
        customMetricsQueryKey,
      );
      if (previousValue && profile) {
        const optimisticCustomMetrics = {
          ...previousValue,
          items: [
            ...previousValue.items,
            {
              ...payload,
              id,
              name: payload.label,
              fromUser: {
                userId: profile.id,
                firstName: profile.profile.firstName,
                lastName: profile.profile.lastName,
              },
            },
          ],
          totalItems: previousValue.totalItems + 1,
          additional: {
            ...previousValue.additional,
            canBeCreated:
              previousValue.totalItems + 1 < previousValue.additional.maxCount,
          },
        };
        queryClient.setQueryData(
          customMetricsQueryKey,
          optimisticCustomMetrics,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseQueryKey,
        exact: false,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(SYSTEM_METRICS_KEY_SEGMENT),
      });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to add custom metric. Please try again'),
        MetricNameExists: t(
          'This custom metric cannot be created because it already exists among the generic metrics available for selection.',
        ),
        CustomMetricNameExists: t(
          'A custom metric with this name already exists. Please use a different name.',
        ),
      }),
    },
  });
};

export const useUpdateCustomMetric = (searchParams?: SearchParams) => {
  const queryClient = useQueryClient();
  const { queryKey: customMetricsQueryKey } =
    DataIngestion.CustomMetrics.getCustomMetricsQueryOptions({
      params: getCustomMetricsParams(searchParams?.search),
    });
  const baseQueryKey = customMetricsQueryKey.slice(0, -1);

  return useEnhancedMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: DataIngestion.CustomMetrics.UpdateCustomMetricPayload;
    }) => {
      await DataIngestion.CustomMetrics.updateCustomMetric({
        urlParams: { id },
        data: payload,
      });
    },
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({
        queryKey: baseQueryKey,
      });

      const previousValue = queryClient.getQueryData<CustomMetricsQueryResult>(
        customMetricsQueryKey,
      );

      if (previousValue) {
        queryClient.setQueryData(customMetricsQueryKey, {
          ...previousValue,
          items: previousValue.items.map((item: CustomMetric) =>
            item.id === id ? { ...item, ...payload } : item,
          ),
        });
      }

      return { previousValue };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(customMetricsQueryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseQueryKey,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(SYSTEM_METRICS_KEY_SEGMENT),
      });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to update custom metric. Please try again'),
        CustomMetricNameExists: t(
          'A custom metric with this name already exists. Please use a different name.',
        ),
        CustomMetricUsedInFileError: t(
          'In use for an ongoing import. Finish or cancel it to make edits.',
        ),
      }),
    },
  });
};

export const useDeleteCustomMetric = (searchParams?: SearchParams) => {
  const queryClient = useQueryClient();
  const { queryKey: customMetricsQueryKey } =
    DataIngestion.CustomMetrics.getCustomMetricsQueryOptions({
      params: getCustomMetricsParams(searchParams?.search),
    });
  const baseQueryKey = customMetricsQueryKey.slice(0, -1);

  return useEnhancedMutation({
    mutationFn: async (id: string) => {
      await DataIngestion.CustomMetrics.deleteCustomMetric({
        urlParams: { id },
      });
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: baseQueryKey,
        exact: false,
      });
    },
    onSuccess: (_, id) => {
      const previousValue = queryClient.getQueryData<CustomMetricsQueryResult>(
        customMetricsQueryKey,
      );

      if (previousValue) {
        const optimisticCustomMetrics = {
          ...previousValue,
          items: previousValue.items.filter((item) => item.id !== id),
          totalItems: previousValue.totalItems - 1,
          additional: {
            ...previousValue.additional,
            canBeCreated:
              previousValue.totalItems - 1 < previousValue.additional.maxCount,
          },
        };
        queryClient.setQueryData(
          customMetricsQueryKey,
          optimisticCustomMetrics,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseQueryKey,
        exact: false,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(SYSTEM_METRICS_KEY_SEGMENT),
      });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to delete custom metric. Please try again'),
        CustomMetricUsedAsKPIError: t(
          'This custom metric is selected as a KPI and cannot be deleted.',
        ),
        CustomMetricUsedInFileError: t(
          'The metric cannot be deleted because it is currently in use.',
        ),
      }),
    },
  });
};
